import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useAdminRequestApproval,
  useAdminRequestReject,
  useGetAllVQAListAdmin,
  useGetAuditorsListForFilters,
  useGetprovidersListForFilters,
  UseGetRfiListForFilters,
} from "../../custom-hooks/queries";
import FIltersDropdown from "../../components/Filters/FiltersDropdown";
import MainSearchBar from "../../components/MainSearchbar";
import SkeletonLoader from "../../components/SkeletonLoader";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import Capabilities from "../../components/Capabilities";
import ScopeWithAdditionalTags from "../../components/ScopeWithAdditionalTags";
import DropdownMenu from "../../components/Dropdown";
import { dateFormat } from "../../utils/helperMethods";
import Pagination from "../../components/CustomPagination";
import Notification from "../../components/Notification";
import { getAssessmentRequestArchiveCommentDisplay } from "../../utils/assessmentRequestGridFields";
import {
  ADMIN_ASSESSMENTS_DEFAULT_SORT_CONFIG,
  getAdminAssessmentRequestDate,
  sortConfigToAdminAssessmentsApiParams,
} from "../../utils/adminAssessmentsGridSort";

export const AdminAssessmentGrid = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState("");
  const [sortConfig, setSortConfig] = useState(
    ADMIN_ASSESSMENTS_DEFAULT_SORT_CONFIG
  );
  const [searchQuery, setSearchQuery] = useState("");
  const searchTimerRef = useRef(null);

  const { data: providers } = useGetprovidersListForFilters();
  const { data: rfis } = UseGetRfiListForFilters();
  const { data: auditors } = useGetAuditorsListForFilters();
  const [selectedIds, setSelectedIds] = useState({
    auditor: [],
    provider: [],
    status: [],
    services: [],
    sponsor: [],
  });

  const {
    mutate: approve,
    error: approveError,
    isError: approveIsError,
  } = useAdminRequestApproval();
  const {
    mutate: reject,
    error: rejectError,
    isError: rejectIsError,
  } = useAdminRequestReject();

  const filters = [
    {
      id: "provider",
      name: "Vendor",
      options: providers,
      total: 1335,
    },

    {
      id: "sponsor",
      name: "Sponsor",
      options: [],
      total: 1335,
    },
    {
      id: "auditor",
      name: "Auditor",
      options: auditors,
      total: 1335,
    },
    {
      id: "status",
      name: "Status",
      options: [
        { statusID: "Accepted", label: "Accepted", checked: false },
        { statusID: "Denied", label: "Denied", checked: false },
        { statusID: "Scheduled", label: "Scheduled", checked: false },
        {
          statusID: "Sponsor Draft Report Available",
          label: "Sponsor Draft Report Available",
          checked: false,
        },
        {
          statusID: "Provider Draft Report Available",
          label: "Vendor Draft Report Available",
          checked: false,
        },
        {
          statusID: "CAPA Response Available",
          label: "CAPA Response Available",
          checked: false,
        },
        {
          statusID: "Final Report Available",
          label: "Final Report Available",
          checked: false,
        },
        {
          statusID: "Pending Admin Review",
          label: "Pending Admin Review",
          checked: false,
        },
      ],
      total: 5,
    },
    {
      id: "services",
      name: "Services",
      options: rfis,
      total: 33,
    },
  ];

  const menuItems = [{ label: "View", href: "#" }];

  const serverSortParams = useMemo(
    () => sortConfigToAdminAssessmentsApiParams(sortConfig),
    [sortConfig]
  );

  const {
    data: requestList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllVQAListAdmin(
    currentPage,
    selectedIds.auditor,
    selectedIds.provider,
    selectedIds.status,
    selectedIds.services,
    selectedIds.sponsor,
    searchQuery,
    serverSortParams
  );

  const errorMessage =
    rejectError?.response?.data?.message ||
    approveError?.response?.data?.message ||
    "An unexpected error occurred.";

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig[key]?.direction === "asc" ? "desc" : "asc";
      return { [key]: { direction: newDirection } };
    });
    setCurrentPage(1);
  };

  // Server already ordered when sort maps to Assessments/Admin sort_by.
  const sortedItems = useMemo(() => {
    const items = requestList?.items || [];
    if (serverSortParams?.orderBy) return items;
    return [...items].sort((a, b) => {
      for (let key in sortConfig) {
        const { direction } = sortConfig[key];
        const valueA =
          key === "requested_on" ? getAdminAssessmentRequestDate(a) : a[key];
        const valueB =
          key === "requested_on" ? getAdminAssessmentRequestDate(b) : b[key];
        if (valueA !== valueB) {
          return direction === "asc"
            ? valueA > valueB
              ? 1
              : -1
            : valueA < valueB
            ? 1
            : -1;
        }
      }
      return 0;
    });
  }, [requestList?.items, sortConfig, serverSortParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedIds]);

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen">
      <div className="sm:flex sm:items-center"></div>
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-5 ">
        <h1 className="font-semibold">Assessments List</h1>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-4">
              <MainSearchBar
                pageKey="adminAssessments"
                placeholder="Search assessments..."
                height="40px"
                width="25rem"
                onValueChange={(value) => {
                  clearTimeout(searchTimerRef.current);
                  searchTimerRef.current = setTimeout(() => {
                    setSearchQuery(value);
                    setCurrentPage(1);
                  }, 300);
                }}
              />
            </div>
            <FIltersDropdown
              rfis={rfis}
              providers={providers}
              setSelectedIds={setSelectedIds}
              selectedIds={selectedIds}
              filters={filters}
            />
            {isLoading ? (
              <SkeletonLoader />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300 table-fixed">
                  <thead className="bg-gray-100 py-3">
                    <tr>
                      <th
                        onClick={() => handleSort("sponsor_name")}
                        scope="col"
                        className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900 space-x-2"
                      >
                        <span>Sponsor</span>
                        {sortConfig.sponsor_name?.direction === "asc" ? (
                          <ChevronUpIcon className="w-4 h-4 inline" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4 inline" />
                        )}
                      </th>
                      <th
                        onClick={() => handleSort("provider_name")}
                        scope="col"
                        className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900 space-x-2"
                      >
                        <span>Vendor</span>
                        {sortConfig.Provider_name?.direction === "asc" ? (
                          <ChevronUpIcon className="w-4 h-4 inline" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4 inline" />
                        )}
                      </th>
                      <th
                        scope="col"
                        className="w-1/6 py-3.5 pl-4 pr-3 text-start text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="w-[8%] px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                      >
                        Services
                      </th>

                      <th
                        scope="col"
                        className="w-[20%] px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                      >
                        Auditor
                      </th>

                      <th
                        onClick={() => handleSort("requested_on")}
                        scope="col"
                        className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900 space-x-2"
                      >
                        <span>Request Date </span>
                        {sortConfig.requested_on?.direction === "asc" ? (
                          <ChevronUpIcon className="w-4 h-4 inline" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4 inline" />
                        )}
                      </th>

                      <th
                        scope="col"
                        className="w-48 px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                      >
                        Archive Comment
                      </th>

                      <th
                        scope="col"
                        className="w-1/5 px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white text-start">
                    {sortedItems?.length === 0 ? (
                      <tr>
                        <td
                          colSpan={12}
                          className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center"
                        >
                          No data available in table
                        </td>{" "}
                      </tr>
                    ) : (
                      sortedItems?.map((item) => (
                        <tr key={item.email}>
                          <td className="whitespace-break-spaces px-3 py-4 text-sm text-gray-500">
                            {item?.sponsor_name}
                          </td>
                          <td className="whitespace-break-spaces px-3 py-4 text-sm text-gray-500">
                            {item?.provider_name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-gray-500 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              {item?.request_status?.toLowerCase() ===
                              "pending" ? (
                                <div className="flex-none rounded-full bg-yellow-400/10 p-1 mr-2">
                                  <div className="h-2 w-2 rounded-full bg-yellow-400"></div>
                                </div>
                              ) : item?.request_status?.toLowerCase() ===
                                "rejected" ? (
                                <div className="flex-none rounded-full bg-red-400/10 p-1 mr-2">
                                  <div className="h-2 w-2 rounded-full bg-red-400"></div>
                                </div>
                              ) : (
                                <div className="flex-none rounded-full  p-1 ">
                                  <div className="h-2 w-2 rounded-full "></div>
                                </div>
                              )}

                              <div className="text-gray-500">
                                {item.vqa_status}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <ScopeWithAdditionalTags
                              services={item.capabilities}
                              additionalScope={item.additional_scope}
                              capabilitiesProps={{
                                icon: true,
                                showLimited: false,
                                badgesPerRow: 3,
                              }}
                            />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {item?.vqa_auditor_name}
                          </td>

                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {dateFormat(getAdminAssessmentRequestDate(item))}
                          </td>

                          <td className="px-3 py-4 text-sm text-gray-500 w-48 max-w-xs">
                            {(() => {
                              const text =
                                getAssessmentRequestArchiveCommentDisplay(item);
                              return text ? (
                                <span
                                  className="italic text-xs line-clamp-2"
                                  title={text}
                                >
                                  {text}
                                </span>
                              ) : (
                                <span className="text-gray-300">--</span>
                              );
                            })()}
                          </td>

                          <td className="px-3 py-4 text-sm">
                            {" "}
                            <DropdownMenu
                              menuItems={menuItems}
                              refetch={refetch}
                              id={item?.vqa_id}
                              approve={approve}
                              reject={reject}
                              status={setStatus}
                              rejectError={rejectError}
                              approveError={approveError}
                              rejectIsError={rejectIsError}
                              approveIsError={approveIsError}
                              navigation={`/Backend/Assessment/${item?.vqa_id}/Manage`}
                              icon={true}
                              docStatus={item?.vqa_status}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {!isError && !isLoading && sortedItems.length > 0 && (
              <div className="mt-5">
                <Pagination
                  totalPages={requestList?.total_pages}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Notification
        show={show}
        setShow={setShow}
        errorMessage={
          rejectError
            ? rejectError?.response.data.message
            : approveError?.response.data.message
        }
        status={status}
      />
    </div>
  );
};
